/**
 * The tooth chart's geometry and encoding.
 *
 * **This replaced the mockup's horseshoe arch on 2026-09-19**, at the client's
 * direction: two straight rows of anatomically-shaped teeth drawn from the
 * facial side, crowns meeting at the occlusal line and roots pointing away from
 * it. See `DECISIONS.md` (UI-TC1). The mockup's `toothCell` — 32 rotated
 * rounded rectangles swept across an ellipse — is deliberately not what this
 * draws any more, and that is the one place in this file where the mockup is
 * *not* the specification.
 *
 * What did not change: FDI order, the four states, the type mapping and the
 * anatomical names. Those are clinical facts rather than design choices.
 */

/**
 * FDI notation. Two digits: quadrant, then position from the midline.
 *
 * Upper right 18→11, upper left 21→28; lower right 48→41, lower left 31→38.
 * **The order is anatomical, not numeric** — each row reads across the mouth as
 * a clinician looks at it, which is why the upper row counts *down* to 11 and
 * then up again from 21.
 */
export const TEETH_UPPER = [
  18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28,
] as const;
export const TEETH_LOWER = [
  48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38,
] as const;

/**
 * **Baby teeth** (BT4) — FDI quadrants 5–8, the same four sides of the mouth
 * as 1–4 and in the same anatomical order, five teeth each: two incisors, a
 * canine and **two molars**. Positions 6–8 do not exist in a child's mouth.
 */
export const PRIMARY_UPPER = [55, 54, 53, 52, 51, 61, 62, 63, 64, 65] as const;
export const PRIMARY_LOWER = [85, 84, 83, 82, 81, 71, 72, 73, 74, 75] as const;

export function isPrimaryTooth(fdiNumber: number): boolean {
  return (
    (PRIMARY_UPPER as readonly number[]).includes(fdiNumber) ||
    (PRIMARY_LOWER as readonly number[]).includes(fdiNumber)
  );
}

/**
 * The baby tooth an adult tooth comes in under, four quadrants on — `11` → `51`,
 * `35` → `75` — or `null` for an adult molar, which no baby tooth comes before.
 * The API holds the same rule (`apps/api/src/patients/primary-teeth.ts`).
 */
export function predecessorOf(fdiNumber: number): number | null {
  const quadrant = Math.floor(fdiNumber / 10);
  const position = fdiNumber % 10;
  if (quadrant < 1 || quadrant > 4 || position < 1 || position > 5) return null;
  return fdiNumber + 40;
}

/** The four states, and the only four (working plan §6.3). */
export const TOOTH_STATES = ["SOUND", "TREATED", "PLANNED", "MISSING"] as const;
export type ToothStateName = (typeof TOOTH_STATES)[number];

/**
 * What the chart draws (BF5) — the same for everyone. `DONE` is the stored
 * `TREATED`, blue whoever did the work; `TO_BE_DONE` is red from charted planned
 * work, an unstarted plan step, or a live booking on the tooth.
 */
export const CHART_TOOTH_STATES = ["SOUND", "TO_BE_DONE", "DONE", "MISSING"] as const;
export type ChartToothStateName = (typeof CHART_TOOTH_STATES)[number];

type ChartStates = Partial<Record<number, ChartToothStateName>>;

/**
 * **The baby tooth an adult tooth has replaced** (BT10), or `null`: with the
 * layer on, an adult incisor, canine or premolar whose baby tooth is marked
 * fallen (MISSING) has taken that baby tooth's place next to the bite, and the
 * baby tooth is off the chart. With the layer off nothing has replaced anything.
 */
export function replacedBaby(
  fdiNumber: number,
  states: ChartStates,
  primaryTeeth: boolean,
): number | null {
  if (!primaryTeeth) return null;
  const baby = predecessorOf(fdiNumber);
  return baby !== null && states[baby] === "MISSING" ? baby : null;
}

/**
 * **The baby tooth an adult tooth is still waiting behind** (BT6), or `null`.
 * Drawn faded in the outer row until that baby tooth is marked fallen.
 *
 * **Anything on its record means it has come in** (BT9): somebody charted,
 * planned or booked work on it, so it is in the mouth whatever the baby tooth
 * says. Only an adult tooth with nothing on it waits.
 */
export function waitingBehind(
  fdiNumber: number,
  states: ChartStates,
  primaryTeeth: boolean,
): number | null {
  if (!primaryTeeth) return null;
  const baby = predecessorOf(fdiNumber);
  if (baby === null || states[baby] === "MISSING") return null;
  return (states[fdiNumber] ?? "SOUND") === "SOUND" ? baby : null;
}

/**
 * The two tiers of one arch with baby teeth drawn, derived from the adult row
 * so both keep its sixteen columns.
 *
 * **Inner** — next to the bite, what is in the mouth: the adult molar in
 * positions 6–8, and in 1–5 the baby tooth, **or the adult tooth that has
 * replaced it** (BT10). **Outer** — the adult tooth still to come above (or
 * below) each baby tooth; **the fallen baby tooth itself once its adult tooth
 * has moved in** (QA4-D10, 2026-10-18 — BT10 left the column empty, and what
 * had been done on the baby tooth was then one panel away); and `null` only
 * over an adult molar, which replaced nothing.
 */
export function tiersOf(
  adultRow: readonly number[],
  states: ChartStates,
): { inner: number[]; outer: (number | null)[] } {
  const replaced = (fdi: number): boolean => replacedBaby(fdi, states, true) !== null;
  return {
    inner: adultRow.map((fdi) => {
      const baby = predecessorOf(fdi);
      return baby === null || replaced(fdi) ? fdi : baby;
    }),
    outer: adultRow.map((fdi) => {
      const baby = predecessorOf(fdi);
      return baby === null ? null : replaced(fdi) ? baby : fdi;
    }),
  };
}

/** Additive marks (UI-TC10). A glyph never decides a colour. */
export const TOOTH_GLYPHS = ["CROWN", "ROOT_CANAL"] as const;
export type ToothGlyphName = (typeof TOOTH_GLYPHS)[number];

/** How a state reads aloud, for the accessible name. */
export const CHART_STATE_LABEL: Record<ChartToothStateName, string> = {
  SOUND: "not worked on",
  TO_BE_DONE: "to be done",
  DONE: "done",
  MISSING: "removed or missing",
};

/** How a glyph reads aloud. A screen reader gets the same information as the eye. */
export const GLYPH_LABEL: Record<ToothGlyphName, string> = {
  CROWN: "crown",
  ROOT_CANAL: "root canal",
};

export type ToothTypeName = "incisor" | "canine" | "premolar" | "molar";

/**
 * Which kind of tooth a number is, from its position digit.
 *
 * Positions 1–2 are incisors, 3 is the canine, 4–5 are premolars and 6–8 are
 * molars. That holds in every quadrant, which is the whole point of FDI's
 * second digit.
 *
 * **Shape encodes type**, so the chart is readable without colour at all — the
 * first of the redundant channels STORY-023 tests.
 */
export function toothType(fdiNumber: number): ToothTypeName {
  const position = fdiNumber % 10;

  if (position <= 2) return "incisor";
  if (position === 3) return "canine";
  // A baby 4 or 5 is a molar: the baby molars are what the premolars replace.
  if (position <= 5) return isPrimaryTooth(fdiNumber) ? "molar" : "premolar";
  return "molar";
}

/**
 * The box height every tooth shares (UI-TC6).
 *
 * **All 32 teeth are the same height, so root tips sit on a straight line just
 * as crowns sit on the occlusal one.** This supersedes UI-TC1, which let roots
 * of different lengths run away from the bite — the client looked at that and
 * asked for the rows to be parallel.
 *
 * Equalising the tips means equalising root *length*, so the anatomical
 * difference between a canine's root and a molar's is no longer drawn. That is
 * the cost of the decision and it was named when it was taken.
 *
 * **54 rather than any other number.** Each silhouette keeps its crown exactly
 * as drawn and only its root was re-cut to reach this height, so the height
 * that moves the fewest roots is the one that distorts the least. At 54 the
 * upper molar and the lower premolar are untouched — which matters for the
 * molar, whose stubby roots are a deliberate legibility choice (UI-TC5) that a
 * taller box would have undone. The upper canine gives up the most, 40.5px of
 * root becoming 28.5px; it has the longest root in the mouth, so any shared
 * height shortens it.
 */
export const TOOTH_HEIGHT = 54;

/**
 * The column each position occupies, by tooth type (UI-TC6).
 *
 * **The two rows are drawn on one 16-column rhythm**, so 18 sits directly above
 * 48 and 28 above 38. Before this, upper and lower teeth carried their own
 * widths — an upper incisor 22px against a lower one's 17 — which left the
 * lower row about 34px narrower and every column drifting further out towards
 * the molars.
 *
 * The column is the **wider** of the two arches at that position, and the tooth
 * is centred in it. So the teeth themselves keep their real widths: a lower
 * incisor is still the narrowest tooth in the mouth, it just sits in the same
 * column as the upper incisor above it. **The rows are aligned, not mirrored** —
 * mirroring was offered and declined (UI-TC6), because it would have widened
 * the lower incisors and given lower molars a third root.
 */
export const COLUMN_WIDTH: Record<ToothTypeName, number> = {
  incisor: 22,
  canine: 22,
  premolar: 22,
  molar: 30,
};

/**
 * The column a tooth stands in. A baby tooth takes **the column of the adult
 * tooth that replaces it**, so 55 sits in 15's column and the two tiers line
 * up — a baby molar does not get a molar's 30px.
 */
export function columnWidth(fdiNumber: number): number {
  const position = isPrimaryTooth(fdiNumber) ? fdiNumber - 40 : fdiNumber;
  return COLUMN_WIDTH[toothType(position)];
}

/**
 * How large a baby tooth is drawn (BT8): the adult silhouette of its type, made
 * smaller, so a baby molar keeps a molar's crown and roots, and the crown cap
 * and canal marks fit it without being drawn twice.
 *
 * Height is three quarters of an adult's. A baby molar is squeezed to 21px so
 * it fits the premolar column it shares; the others keep their proportions.
 */
export const PRIMARY_TOOTH_HEIGHT = 40;

export function primaryToothBox(shape: ToothShape): { width: number; height: number } {
  const ratio = PRIMARY_TOOTH_HEIGHT / TOOTH_HEIGHT;
  return {
    width: shape.key === "molar" ? 21 : Math.round(shape.width * ratio * 10) / 10,
    height: PRIMARY_TOOTH_HEIGHT,
  };
}

export interface ToothShape {
  key: ToothTypeName;
  /** Pixels. The tooth's own drawing box — also its SVG `viewBox`. */
  width: number;
  height: number;
  /** The silhouette, crown *and* root, in that box's coordinates. */
  path: string;
  /**
   * Where crown meets root.
   *
   * **This is no longer drawn** (UI-TC19 — the client found the line across
   * each tooth pointless, reversing UI-TC4). It stays because it is the only
   * record of where a tooth's neck is, and three things read it through
   * `neckYOf`: the canal's pulp chamber, the crown cap's fade, and the cap's
   * scale anchor.
   *
   * **Do not delete the field because nothing renders it.** Losing it means
   * re-deriving the neck from each silhouette by hand, and every mark on the
   * tooth is positioned against it.
   */
  neck: string;
}

/**
 * ── How a tooth is drawn ──────────────────────────────────────────────────
 *
 * Each silhouette is authored **in its final orientation**, so nothing is
 * flipped at render time:
 *
 *   upper — root at the top of the box, crown at the bottom
 *   lower — crown at the top of the box, root at the bottom
 *
 * Both rows then align on their *crown* edge (`align-items` in the stylesheet),
 * which is what puts every crown on the occlusal line while roots of different
 * lengths run away from it — a canine's root is half again a molar's, and that
 * difference is visible in a mouth.
 *
 * Writing sixteen paths instead of eight and flipping would be less code; it
 * would also make a lower incisor a wide upper one upside down, and the two are
 * not the same tooth. Lower incisors are the narrowest teeth in the mouth.
 *
 * The numbers are drawing coordinates, not clinical measurements. They were
 * chosen against the reference the client supplied and then adjusted against
 * the rendered result, which is the only way a silhouette gets judged.
 */

/** Upper: root at the top, crown flaring to a wide incisal edge at the bottom. */
const UPPER_INCISOR = `
  M 11 2.21
  C 13 2.21 14.6 8.84 15.2 16.8
  C 15.7 22.99 16.2 27.41 16.5 30.5
  C 17.6 37 18.9 43 19.2 48
  C 19.35 51 18.2 52.4 15.6 52.4
  L 6.4 52.4
  C 3.8 52.4 2.65 51 2.8 48
  C 3.1 43 4.4 37 5.5 30.5
  C 5.8 27.41 6.3 22.99 6.8 16.8
  C 7.4 8.84 9 2.21 11 2.21
  Z
  `;

/** Upper: the longest root in the mouth, and a single pointed cusp. */
const UPPER_CANINE = `
  M 11 1.41
  C 13.2 1.41 14.7 8.44 15.3 16.89
  C 15.8 22.52 16.3 25.69 16.6 28.5
  C 17.8 34 18.9 39 19 43
  C 19.1 46.5 17 48.5 14.6 50
  C 12.8 51.2 11.9 52.5 11 53.5
  C 10.1 52.5 9.2 51.2 7.4 50
  C 5 48.5 2.9 46.5 3 43
  C 3.1 39 4.2 34 5.4 28.5
  C 5.7 25.69 6.2 22.52 6.7 16.89
  C 7.3 8.44 8.8 1.41 11 1.41
  Z
  `;

/** Upper: one root, and the two-lobed crown that tells it from an incisor. */
const UPPER_PREMOLAR = `
  M 11 2.82
  C 13.4 2.82 15 10.34 15.6 19.75
  C 16 25.39 16.5 28.21 16.8 31.5
  C 18 37 19.2 43 19.3 47
  C 19.4 50.5 17.6 52.5 14.4 52.8
  C 12.9 52.9 11.8 51.6 11 50.2
  C 10.2 51.6 9.1 52.9 7.6 52.8
  C 4.4 52.5 2.6 50.5 2.7 47
  C 2.8 43 4 37 5.2 31.5
  C 5.5 28.21 6 25.39 6.4 19.75
  C 7 10.34 8.6 2.82 11 2.82
  Z
  `;

/**
 * Upper: **three** roots, which is what separates an upper molar from a lower
 * one at a glance. Two buccal and the palatal root between them.
 *
 * The roots are stubby on purpose. Drawn to their true proportion they came out
 * as three thin prongs over a small crown — a rabbit, not a molar — because at
 * 30px across, "anatomically correct" and "legible" are not the same drawing.
 * The crown now takes the larger share, which is also the half a clinician
 * actually points at.
 */
const UPPER_MOLAR = `
  M 6.2 24
  C 5.4 18 4.6 11 4.6 7.5
  C 4.6 3.6 8.4 3 9.2 7
  C 9.9 10.5 10.6 13 11.2 15.5
  C 11.7 12.5 12.2 9 12.8 6.4
  C 13.6 2.8 16.8 2.8 17.4 6.4
  C 18 9 18.5 12.5 19 15.5
  C 19.6 13 20.3 10.5 21 7
  C 21.8 3 25.6 3.6 25.6 7.5
  C 25.6 11 24.8 18 24 24
  C 27 25 28.2 27.6 28.2 32
  C 28.2 40 27.4 47 25.8 50
  C 24.4 52.6 21.4 52.4 19 52
  C 16.4 51.6 13.8 51.6 11.2 52
  C 8.8 52.4 5.8 52.6 4.4 50
  C 2.8 47 2 40 2 32
  C 2 27.6 3.2 25 6.2 24
  Z
  `;

/** Lower: the narrowest tooth in the mouth. Crown at the top, single root below. */
const LOWER_INCISOR = `
  M 3.2 2.8
  L 13.8 2.8
  C 15.6 2.8 16.4 4.6 16 7.6
  C 15.4 12 14 17.5 13.2 21.8
  C 12.9 26.6 12.6 33.45 12.2 40.3
  C 11.8 47.15 10.6 51.03 8.5 51.03
  C 6.4 51.03 5.2 47.15 4.8 40.3
  C 4.4 33.45 4.1 26.6 3.8 21.8
  C 3 17.5 1.6 12 1 7.6
  C 0.6 4.6 1.4 2.8 3.2 2.8
  Z
  `;

/** Lower: pointed cusp at the top, long single root below. */
const LOWER_CANINE = `
  M 10 2
  C 11 3.4 12 4.8 13.8 6.2
  C 16 7.8 17.6 9.8 17.4 13
  C 17.2 17 16.4 21 15.8 25.4
  C 15.4 30.03 14.8 36.64 14.3 42.43
  C 13.8 48.21 12.2 51.85 10 51.85
  C 7.8 51.85 6.2 48.21 5.7 42.43
  C 5.2 36.64 4.6 30.03 4.2 25.4
  C 3.6 21 2.8 17 2.6 13
  C 2.4 9.8 4 7.8 6.2 6.2
  C 8 4.8 9 3.4 10 2
  Z
  `;

/** Lower: two-lobed crown at the top, one root below. */
const LOWER_PREMOLAR = `
  M 10.5 4.2
  C 11.4 3 12.6 2.2 14.2 2.4
  C 16.8 2.7 18.4 4.6 18.3 8
  C 18.2 12 17.2 19 16.5 24.2
  C 16 30 15.3 38 14.8 44
  C 14.3 50 12.7 51.6 10.5 51.6
  C 8.3 51.6 6.7 50 6.2 44
  C 5.7 38 5 30 4.5 24.2
  C 3.8 19 2.8 12 2.7 8
  C 2.6 4.6 4.2 2.7 6.8 2.4
  C 8.4 2.2 9.6 3 10.5 4.2
  Z
  `;

/** Lower: **two** roots, mesial and distal, with the notch between them. */
const LOWER_MOLAR = `
  M 4.6 10
  C 4.6 4.6 6.8 2.4 10 2.2
  C 13 2 16 2 19 2.2
  C 22.2 2.4 24.4 4.6 24.4 10
  C 24.4 16 23.6 22 22.6 25.8
  C 22 32.47 21 41.08 20.2 46.47
  C 19.6 50.77 17.2 51.42 16.6 47.11
  C 16 42.16 15.4 34.63 15 27.74
  C 14.8 26.45 14.2 26.45 14 27.74
  C 13.6 34.63 13 42.16 12.4 47.11
  C 11.8 51.42 9.4 50.77 8.8 46.47
  C 8 41.08 7 32.47 6.4 25.8
  C 5.4 22 4.6 16 4.6 10
  Z
  `;

/**
 * The cervical lines, one per silhouette — **geometry now, not drawing**.
 *
 * UI-TC19 stopped rendering these, but they are still the source of every
 * tooth's neck position: `neckYOf` reads them for the canal's chamber and for
 * the crown cap's fade and scale anchor. Keep them accurate to the silhouette
 * even though nothing paints them.
 *
 * Each spans the tooth at its narrowest point — the neck — stopping short of
 * the outline at both ends so it reads as a groove rather than as a bar welded
 * across the tooth.
 */
const UPPER_INCISOR_NECK = `M 6.1 30.5 L 15.9 30.5`;
const UPPER_CANINE_NECK = `M 6.3 28.5 L 15.7 28.5`;
const UPPER_PREMOLAR_NECK = `M 5.9 31.5 L 16.1 31.5`;
const UPPER_MOLAR_NECK = `M 6.6 24.6 L 23.6 24.6`;
const LOWER_INCISOR_NECK = `M 4.3 21.8 L 12.7 21.8`;
const LOWER_CANINE_NECK = `M 4.7 25.4 L 15.3 25.4`;
const LOWER_PREMOLAR_NECK = `M 5 24.2 L 16 24.2`;
const LOWER_MOLAR_NECK = `M 6.8 25.8 L 22.2 25.8`;

const UPPER_SHAPES: Record<ToothTypeName, ToothShape> = {
  incisor: {
    key: "incisor",
    width: 22,
    height: TOOTH_HEIGHT,
    path: UPPER_INCISOR,
    neck: UPPER_INCISOR_NECK,
  },
  canine: {
    key: "canine",
    width: 22,
    height: TOOTH_HEIGHT,
    path: UPPER_CANINE,
    neck: UPPER_CANINE_NECK,
  },
  premolar: {
    key: "premolar",
    width: 22,
    height: TOOTH_HEIGHT,
    path: UPPER_PREMOLAR,
    neck: UPPER_PREMOLAR_NECK,
  },
  molar: {
    key: "molar",
    width: 30,
    height: TOOTH_HEIGHT,
    path: UPPER_MOLAR,
    neck: UPPER_MOLAR_NECK,
  },
};

const LOWER_SHAPES: Record<ToothTypeName, ToothShape> = {
  incisor: {
    key: "incisor",
    width: 17,
    height: TOOTH_HEIGHT,
    path: LOWER_INCISOR,
    neck: LOWER_INCISOR_NECK,
  },
  canine: {
    key: "canine",
    width: 20,
    height: TOOTH_HEIGHT,
    path: LOWER_CANINE,
    neck: LOWER_CANINE_NECK,
  },
  premolar: {
    key: "premolar",
    width: 21,
    height: TOOTH_HEIGHT,
    path: LOWER_PREMOLAR,
    neck: LOWER_PREMOLAR_NECK,
  },
  molar: {
    key: "molar",
    width: 29,
    height: TOOTH_HEIGHT,
    path: LOWER_MOLAR,
    neck: LOWER_MOLAR_NECK,
  },
};

/**
 * The silhouette for one tooth.
 *
 * Takes the arch as well as the number because an upper molar has three roots
 * and a lower one has two — reading the arch off the FDI quadrant here would
 * work too, and would quietly break the day somebody renders a row on its own.
 */
export function toothShape(fdiNumber: number, isUpper: boolean): ToothShape {
  const table = isUpper ? UPPER_SHAPES : LOWER_SHAPES;
  return table[toothType(fdiNumber)];
}

/**
 * Where the neck sits, read back out of the neck path (UI-TC10).
 *
 * Derived rather than stored beside it, so the glyphs cannot drift away from
 * the line they are positioned against: a tooth re-cut to a new height changes
 * `neck`, and everything here follows it in the same edit.
 */
function neckYOf(shape: ToothShape): number {
  const y = /^\s*M\s+[\d.]+\s+([\d.]+)/.exec(shape.neck)?.[1];
  return y === undefined ? shape.height / 2 : Number(y);
}

/**
 * How much larger the crown cap is drawn than the tooth it caps (UI-TC15),
 * reduced by UI-TC18.
 *
 * A real crown stands slightly proud of the prepared tooth, and the cap has to
 * show that without stopping being the tooth's own shape. **14% was too much**:
 * the client's word for it was "weird", and they were right — at that offset
 * the cap reads as a separate rounded box sitting behind the tooth rather than
 * as a layer on it.
 *
 * 8% keeps the cap unmistakably the same silhouette while still lifting it off
 * the outline, and it also keeps the whole cap inside the drawing box, which
 * 14% did not.
 */
const CROWN_CAP_SCALE = 1.08;

/**
 * The transform that lifts the crown cap off the tooth (UI-TC15).
 *
 * It scales about the **middle of the crown**, so the cap stands equally proud
 * all the way round — over the biting surface as well as out to the sides,
 * which is what a crown does.
 *
 * Anchoring at the occlusal edge was tried first and is worth not going back
 * to: it keeps the cap inside the drawing box, but the cap then coincides with
 * the tooth exactly at the biting edge and flares only towards the neck, which
 * draws a wedge rather than a shell.
 *
 * Which end the crown is depends on the arch (UI-TC2): an upper tooth's crown
 * is at the bottom of its box, a lower tooth's at the top.
 */
export function crownCapTransform(shape: ToothShape, isUpper: boolean): string {
  const neckY = neckYOf(shape);
  const x = shape.width / 2;
  const y = isUpper ? (neckY + shape.height) / 2 : neckY / 2;
  return `translate(${x} ${y}) scale(${CROWN_CAP_SCALE}) translate(${-x} ${-y})`;
}

/**
 * Where the crown cap fades out (UI-TC18).
 *
 * **This replaces the rectangular clip the cap used to be cut with, and the
 * clip is what made it look wrong.** A rectangle trimmed the cap at the neck
 * with a hard horizontal edge and at the box sides with vertical ones, so a
 * shape that is nothing but curves acquired four square corners and stopped
 * following the tooth. The client saw a gold box behind the tooth, because
 * that is what was drawn.
 *
 * A gradient does the same job without the corners: opaque over the biting end
 * of the crown, fading to nothing as it reaches the neck, so the cap ends where
 * a crown's margin ends and **tapers out instead of being cut off**. Nothing
 * clips the sides at all any more — the silhouette is its own boundary.
 *
 * Returned as the two ends of a vertical axis. Which way it runs depends on the
 * arch (UI-TC2): an upper tooth bites at the bottom of its box, a lower at the
 * top. The fade is carried a little past the neck so the last of it lands on
 * root rather than stopping dead on the margin.
 */
export function crownCapFade(shape: ToothShape, isUpper: boolean): { fromY: number; toY: number } {
  const neckY = neckYOf(shape);
  return isUpper ? { fromY: shape.height, toY: neckY - 3 } : { fromY: 0, toY: neckY + 3 };
}

/**
 * ── The canal system (UI-TC14) ─────────────────────────────────────────────
 *
 * One entry per silhouette, in that silhouette's own coordinates. Each is the
 * path of the pulp chamber and the canals together, written as stroke segments
 * so a single `d` can be drawn twice — a near-black casing underneath, the
 * white filling on top (UI-TC17). See `tooth-chart.module.css` for why that second pass exists.
 *
 * **The canal count is the root count, and the root count is anatomy**: three
 * on an upper molar, two on a lower, one on everything else. A multi-rooted
 * tooth gets a crossbar across the chamber joining its canals, which is what
 * makes three separate strokes read as one system rather than three scratches.
 *
 * The apex ends stop short of the silhouette's own root tips on purpose. A
 * canal drawn to the very tip reads as a spike escaping the tooth, and at this
 * size the last pixel of a root is its outline rather than its inside.
 *
 * **Every canal is a curve, not a line** (UI-TC17). Drawn straight they read
 * as scratches ruled down the tooth — the client's words for the incisors were
 * that they did not like them — and a real canal curves. The bow is about
 * 1.5px, which is as much as a root this narrow will take before the casing
 * touches the outline.
 *
 * Coordinates were taken from the silhouettes above — the canals follow the
 * roots they sit in, so an upper molar's splay outward towards its three
 * apices rather than running straight up.
 */
const UPPER_CANALS: Record<ToothTypeName, string> = {
  incisor: `M 11 34 Q 12.6 19 10.7 3.5`,
  canine: `M 11 33 Q 12.6 18.5 10.7 3`,
  premolar: `M 11 35 Q 12.6 20 10.7 4.5`,
  // Three roots: two buccal, the palatal between them. Each curves towards its
  // own apex, so the splay is a fan rather than three parallel strokes.
  molar: [
    `M 8 27 L 22 27`,
    `M 8.5 27 Q 6.8 16.5 6.9 6.5`,
    `M 15 27 Q 16.2 16 15 5.5`,
    `M 21.5 27 Q 23.2 16.5 23.3 6.5`,
  ].join(" "),
};

const LOWER_CANALS: Record<ToothTypeName, string> = {
  incisor: `M 8.5 18 Q 9.9 33 8.2 48.5`,
  canine: `M 10 21 Q 11.4 35 9.7 49`,
  premolar: `M 10.5 20.5 Q 11.9 35 10.2 49`,
  // Two roots, mesial and distal — the feature that tells a lower molar from
  // an upper one at a glance, and the reason UI-TC6 declined a true mirror.
  molar: [`M 9 23 L 20 23`, `M 10 23 Q 8.8 35 10.5 46.5`, `M 19 23 Q 20.2 35 18.5 46.5`].join(" "),
};

/** The canal system for one tooth, as a single path (UI-TC14). */
export function canalPath(shape: ToothShape, isUpper: boolean): string {
  const table = isUpper ? UPPER_CANALS : LOWER_CANALS;
  return table[shape.key];
}

/** `18` → `Upper right third molar`. */
const QUADRANTS: Record<number, string> = {
  1: "Upper right",
  2: "Upper left",
  3: "Lower left",
  4: "Lower right",
};

const POSITIONS: Record<number, string> = {
  1: "central incisor",
  2: "lateral incisor",
  3: "canine",
  4: "first premolar",
  5: "second premolar",
  6: "first molar",
  7: "second molar",
  8: "third molar",
};

/** A baby tooth's five positions (BT4). A baby 4 or 5 is a molar. */
const PRIMARY_POSITIONS: Record<number, string> = {
  1: "baby central incisor",
  2: "baby lateral incisor",
  3: "baby canine",
  4: "baby first molar",
  5: "baby second molar",
};

/**
 * The anatomical name, which is what a clinician says out loud.
 *
 * It is also the accessible name for each tooth: "18" read aloud by a screen
 * reader is a number, while "Upper right third molar" is a tooth.
 */
export function toothName(fdiNumber: number): string {
  if (isPrimaryTooth(fdiNumber)) {
    const side = QUADRANTS[Math.floor(fdiNumber / 10) - 4];
    return `${side ?? "Baby"} ${PRIMARY_POSITIONS[fdiNumber % 10] ?? "tooth"}`;
  }

  const quadrant = QUADRANTS[Math.floor(fdiNumber / 10)];
  const position = POSITIONS[fdiNumber % 10];

  if (quadrant === undefined || position === undefined) return `Tooth ${fdiNumber}`;

  return `${quadrant} ${position}`;
}

/** Every adult tooth, in anatomical order, upper row first. */
export const ALL_TEETH: readonly number[] = [...TEETH_UPPER, ...TEETH_LOWER];

/** Every baby tooth, in anatomical order, upper row first. */
export const ALL_PRIMARY_TEETH: readonly number[] = [...PRIMARY_UPPER, ...PRIMARY_LOWER];

/** True for a number FDI actually assigns to a tooth, adult or baby (BT4). */
export function isValidFdiNumber(value: number): boolean {
  return ALL_TEETH.includes(value) || ALL_PRIMARY_TEETH.includes(value);
}
